import React from 'react';
import { fmt } from '../../../design/format';
import { ChartLegend, ChartTable } from './core';

/**
 * Ranked horizontal bars (mockup VL.charts.hbars), with optional target marker and labelAbove for narrow cards.
 * items: [{ label, value, color?, marker?, suffix?, node? }]  node replaces the plain label.
 */
export function HBars({ items, max: maxProp, format = fmt.int, color, labelAbove = false, labelWidth = '1.3fr', showTable = false }) {
  if (showTable) {
    return (
      <div className="chart">
        <ChartTable head={['Item', 'Value']} rows={items.map((d) => [d.label, format(d.value)])} />
      </div>
    );
  }
  const max = maxProp || Math.max(...items.map((d) => d.value), 0) || 1;
  const fill = (d) => d.color || color || 'var(--series-1)';
  const marker = (d) =>
    d.marker != null ? <div style={{ position: 'absolute', top: -3, bottom: -3, width: 2, background: 'var(--text)', left: `${(d.marker / max) * 100}%` }} /> : null;
  const valueNode = (d) => (
    <>
      {format(d.value)}
      {d.suffix && (
        <span className="muted" style={{ fontWeight: 400 }}>
          {' '}
          {d.suffix}
        </span>
      )}
    </>
  );

  if (labelAbove) {
    return (
      <div className="chart">
        <div className="stack" style={{ gap: 10 }}>
          {items.map((d) => (
            <div key={d.label}>
              <div className="row between small" style={{ gap: 8 }}>
                <span style={{ minWidth: 0 }}>{d.node || d.label}</span>
                <span className="num strong">{valueNode(d)}</span>
              </div>
              <div style={{ height: 8, background: 'var(--surface-3)', borderRadius: 3, position: 'relative', marginTop: 4 }}>
                <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${Math.max(1.5, (d.value / max) * 100)}%`, background: fill(d), borderRadius: '0 3px 3px 0' }} />
                {marker(d)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="chart">
      <div style={{ display: 'grid', gridTemplateColumns: `minmax(0,${labelWidth}) minmax(0,2fr) auto`, gap: '9px 12px', alignItems: 'center' }}>
        {items.map((d) => (
          <React.Fragment key={d.label}>
            <div className="small" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.label}>
              {d.node || d.label}
            </div>
            <div style={{ height: 10, background: 'var(--surface-3)', borderRadius: 3, position: 'relative' }} title={`${d.label}: ${format(d.value)}`}>
              <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${Math.max(1.5, (d.value / max) * 100)}%`, background: fill(d), borderRadius: '0 3px 3px 0' }} />
              {marker(d)}
            </div>
            <div className="small num strong" style={{ textAlign: 'right' }}>
              {valueNode(d)}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/** Sparkline (mockup VL.charts.spark). */
export function Sparkline({ values, width = 72, height = 26, color = 'var(--series-1)' }) {
  if (!values || values.length < 2) return null;
  const W = width;
  const H = height;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const rng = max - min || 1;
  const pts = values.map((v, i) => [(i / (values.length - 1)) * (W - 4) + 2, H - 3 - ((v - min) / rng) * (H - 6)]);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('');
  const lp = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      <path d={`${d}L${lp[0]} ${H}L${pts[0][0]} ${H}Z`} style={{ fill: color, opacity: 0.12 }} />
      <path d={d} style={{ fill: 'none', stroke: color, strokeWidth: 1.5, strokeLinejoin: 'round' }} />
      <circle cx={lp[0]} cy={lp[1]} r={2.5} style={{ fill: color }} />
    </svg>
  );
}

/**
 * 100% diverging rows for sentiment (mockup VL.charts.divRows).
 * keys: [{ name, color }], rows: [{ label, values: number[], right? }]
 */
export function DivergingRows({ keys, rows, showTable = false }) {
  if (showTable) {
    return (
      <div className="chart">
        <ChartTable head={['Row', ...keys.map((k) => k.name)]} rows={rows.map((r) => [r.label, ...r.values.map(fmt.int)])} />
      </div>
    );
  }
  return (
    <div className="chart">
      <ChartLegend items={keys.map((k) => ({ name: k.name, color: k.color }))} />
      <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr) auto', gap: '8px 12px', alignItems: 'center' }}>
        {rows.map((r) => {
          const tot = r.values.reduce((a, b) => a + b, 0) || 1;
          return (
            <React.Fragment key={r.label}>
              <div className="small muted nowrap">{r.label}</div>
              <div style={{ display: 'flex', gap: 2, height: 16 }}>
                {r.values.map((v, i) => (
                  <div
                    key={keys[i].name}
                    title={`${keys[i].name}: ${fmt.pct((v / tot) * 100, 0)}`}
                    style={{
                      flex: v,
                      background: keys[i].color,
                      borderRadius: i === 0 ? '3px 0 0 3px' : i === r.values.length - 1 ? '0 3px 3px 0' : 0,
                    }}
                  />
                ))}
              </div>
              <div className="small num" style={{ textAlign: 'right' }}>
                {r.right || ''}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
